import type {
  ApiReturnUser,
  ApiReturnRole,
} from '@highimpact-frontend/api-types';
import { getOrElse } from '../../../utils/functions';
import ConfirmModal from '../../../components/utils/confirmModal/ConfirmModal';
import CreateUserForm from '../../../components/users/CreateUserForm';
import UserService from '../../../api/services/config/user/UserService';
import { useModal } from '../../../utils/hooks/useModal';
import { useSubmit } from '../../../utils/hooks/useSubmit';
import { useAppDispatch } from '../../../store/hooks';
import { resetExternalState } from '../../../store/data/slice';
import ListDataGrid from '../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { GridColDef } from '@mui/x-data-grid-pro';
import { UserActionsCell } from './UserActionsCell';
import PermissionDecoratorV2 from '../../../components/utils/permission/PermissionDecoratorV2';
import EditUserForm from '../../../components/users/EditUserForm';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

interface FilteredUsersProps {
  users: ApiReturnUser[];
  roles: ApiReturnRole[];
  refresh: () => void;
}

const FilteredUsers: React.FC<FilteredUsersProps> = ({
  users,
  roles,
  refresh,
}) => {
  // use translation here :
  const { t } = useTranslation('components.users');

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const createModal = useModal();
  const editModal = useModal<{
    user: ApiReturnUser | null;
  }>();
  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    userId: number;
  }>();

  const deleteUser = async (id: number) => {
    submit({
      callbacks: [
        async () => await UserService.delete(id),
        () => dispatch(resetExternalState({ external_key: 'users' })),
      ],
      errorMessages: {
        anyError: t(
          "Une erreur est survenue lors de la suppression de l'utilisateur"
        ),
        unicityError: 'Vous avez déjà supprimé ce utilisateur',
      },
      successMessage: t("L'utilisateur a été supprimé avec succès"),
    });
  };

  const headings: Record<string, string> = {
    email: t('Email'),
    telephone: t('Téléphone'),
    role: t('Role'),
  };

  type Row = ReturnType<typeof createRow>;

  const createRow = (user: ApiReturnUser) => {
    return {
      id: getOrElse(user.id),
      userIdentity: `${getOrElse(user.name)} ${getOrElse(user.surname)}`,
      email: getOrElse(user.email),
      telephone: getOrElse(user.tel),
      role: getOrElse(
        roles.find((role: ApiReturnRole) => role.id === user.role_id)?.name
      ),
      actions: String(user.id),
      user,
      onDelete: () => {
        deleteModal.open({
          callback: () => deleteUser(user.id),
          message: t("Êtes-vous sûr de vouloir supprimer l'utilisateur"),
          target: user.name,
          userId: user.id,
        });
      },
      onEdit: () => {
        editModal.open({
          user: user,
        });
      },
    };
  };

  const rows = users.map(createRow);

  const createColumn = (field: keyof Row): GridColDef<Row> => ({
    field,
    headerName: headings[field] ?? field,
    minWidth: 100,
    sortable: true,
    filterable: true,
    headerClassName: 'list-data-grid-style_table-header',
    flex: 1,
  });

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      field: 'userIdentity',
      headerName: t('Utilisateur'),
      sortable: true,
      filterable: true,
      minWidth: 150,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    ...(Object.keys(headings) as (keyof Row)[]).map((key) => createColumn(key)),

    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      renderCell: (params) => <UserActionsCell params={params} />,
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item="configuration_users_create_button">
          <Button
            type="submit"
            // className="form-button-submit"
            variant="contained"
            loadingPosition="end"
            data-testid="submit-project-button"
            onClick={() => createModal.open()}
          >
            {t('Créer un utilisateur')}
          </Button>
        </PermissionDecoratorV2>
      </div>

      <ListDataGrid
        rows={rows}
        columns={columns}
        height="calc(100vh - 80px - 125px)"
        checkboxSelection={false}
        initialSortModel={[{ field: 'name', sort: 'asc' }]}
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />

      {createModal.isOpen && (
        <CreateUserForm
          title={t('Créer un nouveau utilisateur')}
          onCancel={createModal.close}
          open={createModal.isOpen}
        />
      )}

      {editModal.isOpen && (
        <EditUserForm
          title={t("Modifier l'utilisateur")}
          user={editModal.params?.user!}
          isModalOpen={editModal.isOpen}
          onCancel={editModal.close}
        />
      )}

      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback()}
          open={deleteModal.isOpen}
          title={'Confirmation'}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default FilteredUsers;
